import { colorFromUserId } from 'lib/util';
import riot from 'riot';

class CollaboratorsUI {
  constructor() {
    this.collaboratorFocus = {};
    this.collaboratorsInMap = {};
    this.collaboratorStyledFields = {};
  }

  setCanvas(canvas) {
    this.canvas = canvas;
  }

  sendPresence() {
    const focus = this.canvas.getFocusedElement();

    this.canvas.operation.sender.send({
      type: 'presence',
      focus,
    });
  }


  getCollaboratorFocus({ userId }) {
    return this.collaboratorFocus[userId];
  }

  setPresent(userId) {
    this.collaboratorsInMap[userId] = true;
  }

  isPresentCollaborator(userId) {
    return this.collaboratorsInMap[userId];
  }

  unsetPresent(userId) {
    delete this.collaboratorsInMap[userId];
  }

  /*
    remove the collaborator's focus from whereever it currently is
    (returns their mouse pointer to the canvas)
  */
  removeCollaboratorFromField({ user }) {
    if (!this.collaboratorFocus[user.id]) return;

    const { nodeId, regionId, field } = this.collaboratorFocus[user.id];
    if (nodeId) {

      const nodeTag = this.canvas.getNodeTag({ id: nodeId });
      if (nodeTag) {
        if (field === 'title') {
          this.removeCollaboratorTitleStyle(nodeTag,user);
        } else if (field === 'summary') {
          this.removeCollaboratorSummaryStyle(nodeTag,user);
        }
      }
    } else if (regionId) {
      const regionTag = this.canvas.regionTags[regionId];
      if (regionTag) {
        if (field === 'title') {
          this.removeCollaboratorTitleStyle(regionTag,user);
        }
      }
    }
    delete this.collaboratorFocus[user.id];
  }

  setCollaboratorFocusOnField({ user, focus }) {
    const { nodeId, regionId, field } = focus;
    let cursor;
    if (nodeId) {
      const nodeTag = this.canvas.getNodeTag({ id: nodeId });

      if (nodeTag) {
        if (field === 'title') {
          this.setCollaboratorNodeTitleStyle(nodeTag,user);
        } else if (field === 'summary') {
          this.setCollaboratorSummaryStyle(nodeTag,user);
        }
        cursor = nodeTag.root.querySelector(`#cursor-${user.id}`);
      }
    } else if (regionId) {
      const regionTag = this.canvas.regionTags[regionId];
      if (regionTag) {
        if (field === 'title') {
          this.setCollaboratorRegionTitleStyle(regionTag,user);
        }
        cursor = regionTag.root.querySelector(`#cursor-${user.id}`);
      }
    }
    this.collaboratorFocus[user.id] = focus;

    if (cursor) {
      cursor.classList.remove('hidden');
    }
  }


  updateCollaboratorFocusDisplay({user, focus}) {
    if (!user || !user.id) return false;
    let focusedChanged = false;
    if (!focus) {
      /*
        if presence in a field changed from focused to not focused, remove the styling from that field
      */
      if (this.collaboratorFocus[user.id]) {
        this.removeCollaboratorFromField({ user });
      }

    } else if (focus) {

      // if user clicked from one field to another, clear the last field focused
      if (!this.collaboratorFocus[user.id] || (this.collaboratorFocus[user.id].nodeId !== focus.nodeId || this.collaboratorFocus[user.id].field !== focus.field)) {
        this.clearPreviousCollaboratorStyle(user);
      }

      this.setCollaboratorFocusOnField({ user, focus });
    }

    this.collaboratorFocus[user.id] = focus;

    return focusedChanged;
  }

  /*
    clone cursor element
    place clone in cursor wrapper
    hide cursor in canvas
  */
  shuttleCursorToField(user,localRef) {
    const remoteRef = document.getElementById(`cursor-${user.id}`);
    if (!remoteRef) return;
    if (!localRef) return;

    const cursorWrapper = this.getCursorWrapper(localRef);

    const cursorClone = remoteRef.cloneNode(true);
    const existingCursor = document.getElementById(`input-field-cursor-${user.id}`);
    const existingCursorInLocalElement = localRef.querySelector(`#input-field-cursor-${user.id}`);

    // bail if cursor is already where it needs to be
    if (existingCursorInLocalElement) return;

    // kill it if its in another element
    if (existingCursor) {
      existingCursor.remove();
    }

    // put a clone of the cursor in the current element
    cursorClone.id = `input-field-cursor-${user.id}`;
    cursorClone.style.display = '';
    cursorClone.style.opacity = 1;
    remoteRef.style.display = 'none';

    cursorWrapper.appendChild(cursorClone);
  }

  getCursorWrapper(localRef) {
    if (!localRef) return;
    let cursorWrapper = localRef.querySelector('.cursor-wrapper');
    if (cursorWrapper) return cursorWrapper;

    const div = document.createElement('div');
    div.classList.add('cursor-wrapper');
    localRef.appendChild(div);

    return div;
  }

  /* remove local cursor element */
  removeInputCursor({user}) {
    if (!user) {
      console.error('missing params in removeInputCursor',user);
      return;
    }

    // remove cursor from input field
    const cursor = document.getElementById(`input-field-cursor-${user.id}`);
    if (cursor) {
      cursor.remove();
    }

    // show mouse cursor
    const remoteCursor = document.getElementById(`cursor-${user.id}`);
    if (remoteCursor) {
      remoteCursor.style.display = '';
    }
  }

  setCollaboratorNodeTitleStyle(nodeTag,user) {
    if (!nodeTag || !nodeTag.refs) return;
    const { title } = nodeTag.refs;

    clearTimeout(nodeTag.collaboratorTitleEditingTimeout);
    this.clearPreviousCollaboratorStyleIfChanged(user,title);
    if (title && !title.getAttribute('data-collaborator-id')) {
      /* if a user is already in this field, don't change the style again */
      title.setAttribute( 'style', `background: ${colorFromUserId(user.id,0.25)} !important`);
      title.setAttribute('data-collaborator-id',user.id);
      this.collaboratorStyledFields[user.id] = title;
    }
    nodeTag.shuttleCursorToTitle(user);
    /* failsafe if we lose the other user */
    nodeTag.collaboratorTitleEditingTimeout = setTimeout(() => {
      // if emancipation happened, this object causes errors if you call refs on it
      const freshNodeTag = this.canvas.nodeTags[nodeTag.id];
      this.removeCollaboratorTitleStyle(freshNodeTag,user);
    },10000);
  }


  setCollaboratorRegionTitleStyle(regionTag,user) {
    if (!regionTag || !regionTag.refs) return;
    const { title } = regionTag.refs;

    clearTimeout(regionTag.collaboratorTitleEditingTimeout);

    this.clearPreviousCollaboratorStyleIfChanged(user,title);
    if (title && !title.getAttribute('data-collaborator-id')) {
      title.setAttribute( 'style', `background: ${colorFromUserId(user.id,0.25)} !important`);
      title.setAttribute('data-collaborator-id',user.id);
      this.collaboratorStyledFields[user.id] = title;
    }
    regionTag.shuttleCursorToTitle(user);
    /* failsafe if we lose the other user */
    regionTag.collaboratorTitleEditingTimeout = setTimeout(() => {
      this.removeCollaboratorTitleStyle(regionTag,user);
    },10000);
  }


  clearPreviousCollaboratorStyleIfChanged(user,element) {
    if (user && this.collaboratorStyledFields[user.id] && element && this.collaboratorStyledFields[user.id] !== element) {
      this.clearPreviousCollaboratorStyle(user);
    }
  }

  clearPreviousCollaboratorStyle(user) {
    if (!user || !this.collaboratorStyledFields[user.id]) return;
    const element = this.collaboratorStyledFields[user.id];
    element.style.background = '';
    element.removeAttribute('data-collaborator-id');
    element.style.height = '';
    element.style.minHeight = '';
    element.style.minWidth = '';
    delete this.collaboratorStyledFields[user.id];
  }


  setCollaboratorSummaryStyle(nodeTag,user) {
    const { summary } = nodeTag.refs;

    clearTimeout(nodeTag.collaboratorSummaryEditingTimeout);

    //summary.style.background = `${colorFromUserId(user.id,0.25)} !important`;
    this.clearPreviousCollaboratorStyleIfChanged(user,summary);
    if (summary && !summary.getAttribute('data-collaborator-id')) {
      summary.setAttribute( 'style', `background: ${colorFromUserId(user.id,0.25)} !important`);
      summary.style.height = 'auto';
      summary.style.minHeight = '12px';
      summary.style.minWidth = '50%';
      summary.setAttribute('data-collaborator-id',user.id);
      this.collaboratorStyledFields[user.id] = summary;
    }
    nodeTag.shuttleCursorToSummary(user);

    /* failsafe if we lose the other user */
    nodeTag.collaboratorSummaryEditingTimeout = setTimeout(() => {
      // if emancipation happened, this object causes errors if you call refs on it
      const freshNodeTag = this.canvas.nodeTags[nodeTag.id];
      this.removeCollaboratorSummaryStyle(freshNodeTag,user);
    },10000);
  }

  removeCollaboratorTitleStyle(tag, user) {
    clearTimeout(tag.collaboratorTitleEditingTimeout);
    if (!tag || !tag.refs) return;
    const { title } = tag.refs;
    if (!title) return;
    this.removeInputCursor({user});
    if (title.getAttribute('data-collaborator-id') == user.id) {
      title.style.background = '';
      title.removeAttribute('data-collaborator-id');
    }
  }

  removeCollaboratorSummaryStyle(nodeTag,user) {
    if (!nodeTag || !nodeTag.refs) return;
    clearTimeout(nodeTag.collaboratorSummaryEditingTimeout);
    const { summary } = nodeTag.refs;
    if (!summary) return;

    if (summary.getAttribute('data-collaborator-id') == user.id) {
      summary.style.background = '';
      summary.style.height = '';
      summary.style.minHeight = '';
      summary.style.minWidth = '';
      summary.removeAttribute('data-collaborator-id');
    }

    this.removeInputCursor({user});
  }

  conjureElementChangedBanner({nodeTag,regionTag,field,user}) {
    this.dismissElementChangedBanner();
    let cursorWrapper;
    if (nodeTag) {

      if (field === 'title') {
        cursorWrapper = this.getCursorWrapper(nodeTag.refs.titleWrapper);
      } else if (field === 'summary') {
        cursorWrapper = this.getCursorWrapper(nodeTag.refs.summaryWrapper);
      }
    } else if (regionTag) {
      cursorWrapper = this.getCursorWrapper(regionTag.refs.titleContainer);
    }
    cursorWrapper.innerHTML = '';
    if (cursorWrapper) {
      const banner = document.createElement('collaborator-activity-banner');
      cursorWrapper.appendChild(banner);
      const nodeId = nodeTag ? nodeTag.id : null;
      const regionId = regionTag ? regionTag.id : null;

      const mounted = riot.mount(banner,'collaborator-activity-banner',{ nodeId, regionId, field, user, canvas: this.canvas });
      this.collaboratorActivityBannerTag = mounted[0];
    }
  }

  dismissElementChangedBanner() {
    if (this.collaboratorActivityBannerTag) {
      this.collaboratorActivityBannerTag.unmount();
      delete this.collaboratorActivityBannerTag;
    }
  }

}

const cui = new CollaboratorsUI();
window.cui = cui;
export { CollaboratorsUI, cui };



// WEBPACK FOOTER //
// ./starling/src/lib/collaborators-ui.js